import { computed, ref } from 'vue';
import { message } from 'ant-design-vue';

export const CUSTOM_OPTION_VALUE = 'custom';
export const MAX_CUSTOM_VALUE = 999999999;

export type CustomOptionValue = typeof CUSTOM_OPTION_VALUE;
export type StorageOptionValue = number | CustomOptionValue;

export interface StorageExpansionValue {
  capacityGB: number;
  months: number;
}

export interface UseStorageExpansionOptions {
  value?: StorageExpansionValue;
  onChange?: (value: StorageExpansionValue) => void;
}

export const capacityOptions = [
  {
    label: '100GB',
    value: 100,
  },
  {
    label: '200GB',
    value: 200,
  },
  {
    label: '500GB',
    value: 500,
  },
  {
    label: '1TB',
    value: 1000,
  },
  {
    label: '2TB',
    value: 2000,
  },
];

export const durationOptions = [
  {
    label: '1个月',
    value: 1,
  },
  {
    label: '2个月',
    value: 2,
  },
  {
    label: '3个月',
    value: 3,
  },
  {
    label: '6个月',
    value: 6,
  },
  {
    label: '1年',
    value: 12,
  },
];

export function filterPositiveIntegerInput(value: string | number | null | undefined) {
  const digits = String(value ?? '').replace(/\D/g, '');

  if (digits.length > 9) {
    return String(MAX_CUSTOM_VALUE);
  }

  return digits;
}

function normalizeIntegerInput(
  value: string | number | null | undefined,
  min: number,
  max: number
) {
  const digits = filterPositiveIntegerInput(value);

  if (!digits) {
    return '';
  }

  const numberValue = Math.min(Math.max(Number(digits), min), max);

  return String(numberValue);
}

export function normalizeCapacityInput(value: string | number | null | undefined) {
  const normalized = normalizeIntegerInput(value, 100, MAX_CUSTOM_VALUE);

  if (!normalized) {
    return '';
  }

  return String(Math.max(100, Math.floor(Number(normalized) / 100) * 100));
}

export function normalizeDurationInput(value: string | number | null | undefined) {
  return normalizeIntegerInput(value, 1, MAX_CUSTOM_VALUE);
}

export function useStorageExpansion(options: UseStorageExpansionOptions = {}) {
  const activeCapacity = ref<StorageOptionValue>(
    options.value?.capacityGB || capacityOptions[0].value
  );
  const activeDuration = ref<StorageOptionValue>(options.value?.months || durationOptions[0].value);
  const customCapacity = ref('');
  const customDuration = ref('');

  const capacityValue = computed(() => {
    if (activeCapacity.value !== CUSTOM_OPTION_VALUE) {
      return activeCapacity.value;
    }

    return Number(customCapacity.value || capacityOptions[0].value);
  });

  const durationValue = computed(() => {
    if (activeDuration.value !== CUSTOM_OPTION_VALUE) {
      return activeDuration.value;
    }

    return Number(Number(customDuration.value) * 12 || durationOptions[0].value);
  });

  function syncChange() {
    options.onChange?.({
      capacityGB: capacityValue.value,
      months: durationValue.value,
    });
  }

  function selectCapacity(value: number) {
    activeCapacity.value = value;
    customCapacity.value = '';
    syncChange();
  }

  function selectDuration(value: number) {
    activeDuration.value = value;
    customDuration.value = '';
    syncChange();
  }

  function activateCustomCapacity() {
    activeCapacity.value = CUSTOM_OPTION_VALUE;
  }

  function activateCustomDuration() {
    activeDuration.value = CUSTOM_OPTION_VALUE;
  }

  function updateCustomCapacity(value: string | number) {
    activateCustomCapacity();
    customCapacity.value = filterPositiveIntegerInput(value);
  }

  function updateCustomDuration(value: string | number) {
    activateCustomDuration();
    customDuration.value = filterPositiveIntegerInput(value);
  }

  function commitCustomCapacity() {
    if (activeCapacity.value === CUSTOM_OPTION_VALUE) {
      const val = normalizeCapacityInput(customCapacity.value) || '100';
      if (customCapacity.value !== val) {
        message.warning('请输入100整数倍');
      }
      customCapacity.value = val;
      syncChange();
    }
  }

  function commitCustomDuration() {
    if (activeDuration.value === CUSTOM_OPTION_VALUE) {
      customDuration.value = normalizeDurationInput(customDuration.value) || '1';
      syncChange();
    }
  }

  return {
    activeCapacity,
    activeDuration,
    customCapacity,
    customDuration,
    capacityValue,
    durationValue,
    selectCapacity,
    selectDuration,
    activateCustomCapacity,
    activateCustomDuration,
    updateCustomCapacity,
    updateCustomDuration,
    commitCustomCapacity,
    commitCustomDuration,
  };
}
